import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Button,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Card,
  CardContent,
  CardActions,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Visibility as ViewIcon,
  RateReview as ReviewIcon,
  Description as ReportIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../contexts/AuthContext';

const MyCases = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Status colors for chips
  const statusColors = {
    'preparation': 'default',
    'sent_to_client': 'info',
    'client_submitted': 'warning',
    'first_review': 'primary',
    'second_review': 'primary',
    'final_review': 'secondary',
    'draft_report': 'secondary',
    'completed': 'success'
  };

  // Status labels for display
  const statusLabels = {
    'preparation': 'Preparation',
    'sent_to_client': 'Sent to Client',
    'client_submitted': 'Client Submitted',
    'first_review': 'First Review',
    'second_review': 'Second Review',
    'final_review': 'Final Review',
    'draft_report': 'Draft Report',
    'completed': 'Completed'
  };

  // Fetch cases on component mount
  useEffect(() => {
    const fetchCases = async () => {
      setLoading(true);
      console.log('Fetching my cases...');
      console.log('Token:', localStorage.getItem('token'));
      console.log('User:', user);
      
      try {
        console.log('API URL:', `${process.env.REACT_APP_API_URL}/audit/my-cases`);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/audit/my-cases`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        console.log('My cases response:', response.data);
        setCases(response.data || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching cases:', err);
        console.log('Response:', err.response ? err.response.data : 'No response data');
        setError('Failed to load cases. ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, [user]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Filter cases based on active tab
  const getFilteredCases = () => {
    switch (tabValue) {
      case 0: // All cases
        return cases;
      case 1: // Pending action
        return cases.filter(c => {
          if (user.role === 'manager') {
            return ['preparation', 'second_review', 'final_review'].includes(c.status);
          } else if (c.first_reviewer_id === user.id) {
            return c.status === 'client_submitted';
          } else if (c.second_reviewer_id === user.id) {
            return c.status === 'first_review';
          }
          return false;
        });
      case 2: // In progress
        return cases.filter(c => {
          return ['sent_to_client', 'client_submitted', 'first_review', 'second_review'].includes(c.status);
        });
      case 3: // Completed
        return cases.filter(c => c.status === 'completed');
      default:
        return cases;
    }
  };

  // Navigate to case details
  const handleViewCase = (id) => {
    navigate(`/audit/cases/${id}`);
  };

  // Navigate to review interface
  const handleReviewCase = (id) => {
    navigate(`/audit/cases/${id}/review`);
  };

  // Navigate to report generator
  const handleGenerateReport = (id) => {
    navigate(`/audit/cases/${id}/report`);
  };

  // Determine if user can review this case
  const canReview = (auditCase) => {
    if (user.role === 'administrator') return true;
    
    if (auditCase.first_reviewer_id === user.id && auditCase.status === 'client_submitted') {
      return true;
    }
    
    if (auditCase.second_reviewer_id === user.id && auditCase.status === 'first_review') {
      return true;
    }
    
    return false;
  };

  // Determine if user can generate report for this case
  const canGenerateReport = (auditCase) => {
    if (user.role === 'administrator') return true;
    
    if ((user.role === 'manager' || auditCase.manager_id === user.id) && 
        ['second_review', 'final_review', 'draft_report'].includes(auditCase.status)) {
      return true;
    }
    
    return false;
  };

  // Render case list
  const renderCaseList = () => {
    // Ensure cases is an array before filtering
    if (!Array.isArray(cases)) {
      console.error('Cases is not an array:', cases);
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">Error: Unable to process case data.</Typography>
        </Box>
      );
    }
    
    const filteredCases = getFilteredCases();
    
    if (filteredCases.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">No cases found for this filter.</Typography>
          {user.role === 'manager' && (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => navigate('/audit/cases')}
              sx={{ mt: 2 }}
            >
              Create New Case
            </Button>
          )}
        </Box>
      );
    }
    
    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Firm</TableCell>
              <TableCell>Review Period</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCases.map((auditCase) => (
              <TableRow key={auditCase.id}>
                <TableCell>{auditCase.title || 'Untitled Case'}</TableCell>
                <TableCell>{auditCase.firm?.name || 'No Firm Assigned'}</TableCell>
                <TableCell>
                  {auditCase.review_period_start ? new Date(auditCase.review_period_start).toLocaleDateString() : 'N/A'} - 
                  {auditCase.review_period_end ? new Date(auditCase.review_period_end).toLocaleDateString() : 'N/A'}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={statusLabels[auditCase.status] || auditCase.status || 'Unknown'} 
                    color={statusColors[auditCase.status] || 'default'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>
                  <IconButton 
                    color="primary" 
                    onClick={() => handleViewCase(auditCase.id)}
                    title="View case details"
                  >
                    <ViewIcon />
                  </IconButton>
                  
                  {canReview(auditCase) && (
                    <IconButton 
                      color="secondary" 
                      onClick={() => handleReviewCase(auditCase.id)}
                      title="Review case"
                    >
                      <ReviewIcon />
                    </IconButton>
                  )}
                  
                  {canGenerateReport(auditCase) && (
                    <IconButton 
                      color="success" 
                      onClick={() => handleGenerateReport(auditCase.id)}
                      title="Generate report"
                    >
                      <ReportIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render dashboard cards
  const renderDashboard = () => {
    const pendingAction = cases.filter(c => {
      if (user.role === 'manager') {
        return ['preparation', 'second_review', 'final_review'].includes(c.status);
      } else if (c.first_reviewer_id === user.id) {
        return c.status === 'client_submitted';
      } else if (c.second_reviewer_id === user.id) {
        return c.status === 'first_review';
      }
      return false;
    }).length;
    
    const inProgress = cases.filter(c => 
      ['sent_to_client', 'client_submitted', 'first_review', 'second_review'].includes(c.status)
    ).length;
    
    const completed = cases.filter(c => c.status === 'completed').length;
    
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Cases
              </Typography>
              <Typography variant="h4" component="div">
                {cases.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Pending Action
              </Typography>
              <Typography variant="h4" component="div" color="warning.main">
                {pendingAction}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                In Progress
              </Typography>
              <Typography variant="h4" component="div" color="info.main">
                {inProgress}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4" component="div" color="success.main">
                {completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          My Cases
        </Typography>
        {user.role === 'manager' && (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/audit/cases')}
          >
            Manage Cases
          </Button>
        )}
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      ) : (
        <Box>
          {renderDashboard()}
          
          <Paper sx={{ mb: 3 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="All Cases" />
              <Tab label="Pending Action" />
              <Tab label="In Progress" />
              <Tab label="Completed" />
            </Tabs>
            
            <Divider />
            
            {renderCaseList()}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default MyCases;
